// src/components/eframes/EFrame135.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, Card } from "../ui";
import { ImportantDocumentsIcon } from "../ui/Icons";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import { unifiedContactService } from "../../services/unifiedContactService";
import { formatPhoneNumberOnly, parsePhoneNumber } from "../../utils/phoneFormat";
import BackToExecutorDashboardButton from "./BackToExecutorDashboardButton";

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

interface ContactEntry {
  id?: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  relationship?: string;
  documents?: Array<{ name: string; url: string }>;
}

interface SummaryData {
  name: string;
  email: string;
  phone: string;
  address: string;
  contacts: ContactEntry[];
  accountEntries: unknown[];
}

const EFrame135: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const planOwnerName = getPlanOwnerName(session.name);

  const [summaryData, setSummaryData] = useState<SummaryData>({
    name: "Not provided",
    email: "Not provided",
    phone: "Not provided",
    address: "Not provided",
    contacts: [],
    accountEntries: [],
  });

  useEffect(() => {
    let isMounted = true;

    const loadSummaryData = async () => {
      let fetchedContacts: ContactEntry[] = [];

      try {
        const data = estateId
          ? await unifiedContactService.getContactsForEstate(estateId)
          : await unifiedContactService.getAllContacts();
        fetchedContacts = data;
      } catch (error) {
        console.error("[EFrame135] Error loading contacts:", error);
      }

      const aboutData = session.taskProgress?.about?.answers ?? {};

      const address =
        aboutData.street && aboutData.city && aboutData.state && aboutData.zip
          ? `${aboutData.street}, ${aboutData.city}, ${aboutData.state} ${aboutData.zip}`
          : "Not provided";

      const phoneValue: string | undefined = aboutData.phone ?? session.phone;

      let formattedPhone = "Not provided";

      if (phoneValue) {
        try {
          const parsed = parsePhoneNumber(phoneValue);
          formattedPhone = `${parsed.countryCode} ${formatPhoneNumberOnly(
            parsed.phoneNumber
          )}`;
        } catch {
          formattedPhone = phoneValue;
        }
      }

      const accessAnswers = session.taskProgress?.["account-access"]?.answers ?? {};
      const entries = accessAnswers.entries ?? [];

      const nextData: SummaryData = {
        name: session.name || "Not provided",
        email: session.email || "Not provided",
        phone: formattedPhone,
        address,
        contacts: fetchedContacts,
        accountEntries: entries,
      };

      if (isMounted) {
        setSummaryData(nextData);
      }
    };

    loadSummaryData();

    return () => {
      isMounted = false;
    };
  }, [session]);

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="mb-6 flex justify-start">
          <BackToExecutorDashboardButton />
        </div>
        <ResponsiveHeader
          title={`Thanks ${planOwnerName}! Here's a summary of the plan so far.`}
        />

        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold text-stone-800 mb-4">
              Overview
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center border-r border-stone-100">
                <div className="text-2xl font-bold text-cyan-900">
                  {summaryData.contacts.length}
                </div>
                <div className="text-sm text-stone-600 font-medium">
                  Contacts Entered
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-green-700">
                  {summaryData.accountEntries.length}
                </div>
                <div className="text-sm text-stone-600 font-medium">
                  Account Instructions Entered
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Basic Information
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-stone-600">
                  Name
                </label>
                <p className="text-stone-800">{summaryData.name}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-600">
                  Email
                </label>
                <p className="text-stone-800">{summaryData.email}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-600">
                  Phone
                </label>
                <p className="text-stone-800">{summaryData.phone}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-600">
                  Address
                </label>
                <p className="text-stone-800">{summaryData.address}</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Contacts Synced ({summaryData.contacts.length})
              </h3>
            </div>

            {summaryData.contacts.length > 0 ? (
              <div
                className={
                  summaryData.contacts.length > 3
                    ? "max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-stone-200"
                    : ""
                }
              >
                <div className="space-y-3">
                  {summaryData.contacts.map((contact, idx) => (
                    <div
                      key={contact.id || idx}
                      className="bg-stone-50 rounded-lg p-4"
                    >
                      <div className="flex justify-between items-start gap-3">
                        <h4 className="font-medium text-stone-800">
                          {contact.firstName} {contact.lastName}
                        </h4>

                        {contact.relationship && (
                          <span className="text-[13px] text-stone-700">
                            {capitalize(contact.relationship)}
                          </span>
                        )}
                      </div>

                      <div className="mt-2 text-stone-500 text-sm space-y-0.5">
                        {contact.email && <p>Email: {contact.email}</p>}
                        {contact.phone && <p>Phone: {contact.phone}</p>}
                      </div>

                      {contact.documents && contact.documents.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-stone-200/60">
                          <p className="text-cyan-600 text-[13px] font-bold mb-1">
                            {contact.documents.length} document(s) attached
                          </p>

                          <div className="space-y-1">
                            {contact.documents.map((doc, dIdx) => (
                              <div
                                key={dIdx}
                                className="flex items-center gap-2 py-1"
                              >
                                <ImportantDocumentsIcon className="w-3.5 h-3.5 text-cyan-600 opacity-70" />
                                <span className="text-sm text-stone-700 truncate">
                                  {doc.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                <p>No contacts available.</p>
              </div>
            )}
          </Card>

          <Card>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Access and Login Information Instructions (
                {summaryData.accountEntries.length})
              </h3>
            </div>

            {summaryData.accountEntries.length > 0 ? (
              <div
                className={
                  summaryData.accountEntries.length > 3
                    ? "max-h-[350px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-stone-200"
                    : ""
                }
              >
                <div className="space-y-3">
                  {summaryData.accountEntries.map((entry, idx) => (
                    <div
                      key={entry.id || idx}
                      className="bg-stone-50 rounded-lg p-4"
                    >
                      <h4 className="font-medium text-stone-800">
                        {entry.accountName}
                      </h4>

                      <div className="mt-1 flex flex-col gap-0.5 text-stone-500 text-[15px]">
                        <p>
                          Storage: {entry.storageLocation}
                          {entry.storageLocation === "Other" &&
                            `: ${entry.otherLocationDetails}`}
                        </p>

                        <p>MFA: {entry.mfaEnabled ? "Enabled" : "Disabled"}</p>

                        {entry.executorInstructions && (
                          <p>
                            Executor Access Instructions:{" "}
                            {entry.executorInstructions}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                <p>No access instructions available.</p>
              </div>
            )}
          </Card>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame135;